import { PropsWithChildren } from 'react';
import {
  MdCheckCircleOutline,
  MdClose,
  MdOutlineErrorOutline,
  MdOutlineInfo,
  MdOutlineLink,
  MdOutlineWarning,
  MdOutlineWarningAmber,
} from 'react-icons/md';
import { PiWarningCircle } from 'react-icons/pi';
import { IconButton, useDisclosure } from '@ds';
import { LightsAndSirens, ErrorIcon, DenovoOrgan, EnhanceOrgan } from '@icons';
import cn from 'classnames';

const COLOR_STYLES: Record<string, Record<string, string>> = {
  error: {
    bg: 'bg-danger-70',
    border: 'border-danger-40',
    icon: 'text-danger-40',
    text: 'text-danger-5',
  },
  redWarning: {
    bg: 'bg-danger-70',
    border: 'border-danger-40',
    icon: 'text-danger-40',
    text: 'text-danger-5',
  },
  lightSirens: {
    bg: 'bg-info-70',
    border: 'border-info-40',
    icon: 'text-info-40',
    text: 'text-info-5',
  },
  warning: {
    bg: 'bg-warning-80',
    border: 'border-warning-40',
    icon: 'text-warning-40',
    text: 'text-warning-5',
  },
  outlineWarning: {
    bg: 'bg-warning-80',
    border: 'border-warning-40',
    icon: 'text-warning-40',
    text: 'text-warning-5',
  },
  cross: {
    bg: 'bg-danger-70',
    border: 'border-danger-40',
    icon: 'text-danger-40',
    text: 'text-danger-5',
  },
  info: {
    bg: 'bg-info-70',
    border: 'border-info-40',
    icon: 'text-info-40',
    text: 'text-info-5',
  },
  success: {
    bg: 'bg-success-70',
    border: 'border-success-40',
    icon: 'text-success-40',
    text: 'text-success-5',
  },
  infoWarning: {
    bg: 'bg-info-70',
    border: 'border-info-40',
    icon: 'text-info-40',
    text: 'text-info-5',
  },
  multiStop: {
    bg: 'bg-base-55',
    border: '',
    icon: 'text-base-20',
    text: 'text-base-10',
  },
  denovoOrgan: {
    bg: 'bg-warning-80',
    border: 'border-base-10',
    icon: '',
    text: 'text-base-10',
  },
  enhanceOrgan: {
    bg: 'bg-warning-80',
    border: 'border-base-10',
    icon: '',
    text: 'text-base-10',
  },
};

type InfoBannerProps = {
  title?: string;
  iconType?:
    | 'error'
    | 'lightSirens'
    | 'warning'
    | 'outlineWarning'
    | 'cross'
    | 'info'
    | 'success'
    | 'multiStop'
    | 'denovoOrgan'
    | 'enhanceOrgan'
    | 'infoWarning'
    | 'redWarning';
  iconPosition?: 'start' | 'center';
  withClose?: boolean;
  className?: string;
};

export const InfoBanner = ({
  title,
  iconType = 'info',
  iconPosition = 'center',
  children,
  withClose = false,
  className,
}: PropsWithChildren<InfoBannerProps>) => {
  const { isOpen, onClose } = useDisclosure({ defaultOpen: true });
  const colors = COLOR_STYLES[iconType];

  const getIcon = () => {
    switch (iconType) {
      case 'info':
        return <MdOutlineInfo size={24} role="img" aria-label="Info icon" />;
      case 'cross':
        return <ErrorIcon width={24} height={24} role="img" aria-label="Cross icon" />;
      case 'lightSirens':
        return <LightsAndSirens width={16} height={16} role="img" aria-label="Light sirens icon" />;
      case 'warning':
        return <MdOutlineWarning size={24} className={colors.icon} role="img" aria-label="Warning icon" />;
      case 'outlineWarning':
        return <MdOutlineWarningAmber size={24} className={colors.icon} role="img" aria-label="Warning icon" />;
      case 'redWarning':
        return <MdOutlineWarning size={24} className={colors.icon} role="img" aria-label="Warning icon" />;
      case 'success':
        return <MdCheckCircleOutline size={24} className={colors.icon} role="img" aria-label="Success icon" />;
      case 'multiStop':
        return <MdOutlineLink size={24} className={colors.icon} role="img" aria-label="Multi stop icon" />;
      case 'denovoOrgan':
        return <DenovoOrgan width={26} height={24} role="img" aria-label="Denovo trial organ" />;
      case 'enhanceOrgan':
        return <EnhanceOrgan width={26} height={24} role="img" aria-label="Enhance trial organ" />;
      case 'infoWarning':
        return <PiWarningCircle size={24} className={colors.icon} role="img" aria-label="Success warning icon" />;
      default:
        return <MdOutlineErrorOutline size={24} className={colors.icon} role="img" aria-label="Error icon" />;
    }
  };

  return isOpen ? (
    <div
      className={cn(
        `flex rounded-lg border-l-8 px-2 py-2 gap-2`,
        colors.border,
        colors.bg,
        { 'items-start': iconPosition === 'start' },
        { 'items-center': iconPosition === 'center' },
        className,
      )}
    >
      <div className={`shrink-0 ${colors.icon}`}>{getIcon()}</div>
      <div className="flex flex-1 flex-col">
        {!!title && <span className={`text-m-sb ${colors.text}`}>{title}</span>}
        <div className={`${colors.text} text-m-r`}>{children}</div>
      </div>
      {withClose && (
        <IconButton
          className="
            group my-1 bg-transparent border-0
            hover:bg-transparent active:bg-transparent
            focus-visible:outline-none focus-visible:ring-0
            data-[hover=true]:bg-transparent data-[pressed=true]:bg-transparent
          "
          size="xs"
          variant="outline"
          startContent={
            <MdClose
              className="
            h-[20px]! w-[20px]!
            transform-gpu transition-transform duration-150 ease-out
            group-hover:scale-[1.3] 
            group-active:scale-100 
            focus:scale-[1.2]
          "
            />
          }
          onPress={onClose}
        />
      )}
    </div>
  ) : null;
};
